import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';
import { LIVERY } from '../themes/livery';

/**
 * La Chiva, the 1978 Cessna of Turbina painted like a Colombian chiva, and La Bolsa hanging under it
 * (GDD §4.1, issue #66). Low-poly primitives with flat shading, shared by the title screen and the flight.
 * The airframe is 1.6 x 0.7 u seen from the side and the hitbox (1.2 x 0.5 u) sits at its centre; La Bolsa
 * hangs below and is not part of the hitbox. The escapulario is inside the cabin and is not modelled.
 */
export interface LaChiva {
  group: Group;
  parts: { airframe: Group; wing: Mesh; tapePatch: Mesh; propeller: Mesh; bag: Group };
  dispose(): void;
}

export function buildLaChiva(): LaChiva {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const yellow = paint(LIVERY.yellow);
  const red = paint(LIVERY.red);
  const blue = paint(LIVERY.blue);
  const dark = paint(LIVERY.propeller);

  const mesh = <G extends BufferGeometry>(geometry: G, material: Material, parent: Object3D, name?: string) => {
    geometries.push(geometry);
    const result = new Mesh(geometry, material);
    if (name) result.name = name;
    parent.add(result);
    return result;
  };

  const group = new Group();
  group.name = 'player';

  // Airframe: fuselage, stripe, nose, tail and landing gear. 1.6 x 0.7 u from the nose cone to the tail.
  const airframe = new Group();
  airframe.name = 'player.airframe';
  const fuselage = mesh(new CylinderGeometry(0.2, 0.25, 1.3, 6), yellow, airframe);
  fuselage.rotation.z = Math.PI / 2;
  const stripe = mesh(new CylinderGeometry(0.255, 0.255, 0.3, 6), red, airframe);
  stripe.rotation.z = Math.PI / 2;
  stripe.position.x = 0.1;
  const nose = mesh(new ConeGeometry(0.25, 0.15, 6), blue, airframe);
  nose.rotation.z = -Math.PI / 2;
  nose.position.x = 0.725;
  mesh(new BoxGeometry(0.3, 0.3, 0.05), red, airframe).position.set(-0.65, 0.2, 0);
  mesh(new BoxGeometry(0.25, 0.04, 0.7), blue, airframe).position.set(-0.62, 0.05, 0);
  mesh(new BoxGeometry(0.04, 0.2, 0.04), dark, airframe).position.set(0.2, -0.25, 0);
  mesh(new BoxGeometry(0.22, 0.06, 0.12), dark, airframe).position.set(0.2, -0.32, 0);
  group.add(airframe);

  const wing = mesh(new BoxGeometry(0.5, 0.05, 2.4), blue, group, 'player.wing');
  wing.position.set(0.1, 0.22, 0);

  // The patch of tape on the wing (GDD §4.1): a neutral tone.
  const tapePatch = mesh(new BoxGeometry(0.2, 0.012, 0.3), paint(LIVERY.tape), group, 'player.tapePatch');
  tapePatch.position.set(0.1, 0.25, 0.6);

  const propeller = mesh(new BoxGeometry(0.04, 0.7, 0.08), dark, group, 'player.propeller');
  propeller.position.x = 0.83;

  // La Bolsa hangs from a pivot under the fuselage and swings around it (bag-swing.ts).
  const bag = new Group();
  bag.name = 'player.bag';
  bag.position.set(0.05, -0.2, 0);
  mesh(new BoxGeometry(0.02, 0.3, 0.02), paint(LIVERY.rope), bag).position.y = -0.15;
  const sack = mesh(new IcosahedronGeometry(0.2, 0), paint(LIVERY.bag), bag);
  sack.position.y = -0.55;
  sack.scale.set(1.15, 1, 1);
  group.add(bag);

  return {
    group,
    parts: { airframe, wing, tapePatch, propeller, bag },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
